import React, { useEffect, useMemo, useRef, useState } from "react";
import AdminHeader from "../components/AdminHeader";
import { getAllProjects, getInteractionCounts } from "../api";

const fmt = (n) => new Intl.NumberFormat().format(n ?? 0);
const DEFAULT_START = "2025-07-01"; 
const DEFAULT_END = "2025-07-31";
const ALL_ID = "ALL";

const csvEscape = (val) => {
  const s = String(val ?? "");
  if (s.includes('"') || s.includes(",") || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};
const rowsToCSV = (rows, totalsRow) => {
  const hdr = ["Project", "Bot Messages", "Human Messages", "Total Interactions", "# of Conversations"];
  const lines = [hdr.map(csvEscape).join(",")];
  rows.forEach((r) => {
    lines.push(
      [r.name, r.bot, r.human, r.interactions, r.conversations].map(csvEscape).join(",")
    );
  });
  if (totalsRow) {
    lines.push(
      [
        "TOTAL",
        totalsRow.total_no_of_bot_messages,
        totalsRow.total_no_of_human_messages,
        totalsRow.total_interactions,
        totalsRow.number_of_conversation,
      ].map(csvEscape).join(",")
    );
  }
  return lines.join("\n");
};

const CountSummaryPage = () => {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState("");

  const [query, setQuery] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  const displayProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? projects.filter((p) => (p.name || "").toLowerCase().includes(q))
      : projects;
    const sorted = [...filtered].sort((a, b) => {
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      return sortAsc ? an.localeCompare(bn) : bn.localeCompare(an);
    });
    return sorted;
  }, [projects, query, sortAsc]);

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const isAll = selectedProjectId === ALL_ID;
  const selectedProject = useMemo(
    () =>
      isAll
        ? { id: ALL_ID, name: "All Projects" }
        : projects.find((p) => String(p.id) === String(selectedProjectId)) || null,
    [projects, selectedProjectId, isAll]
  );

  const [startDate, setStartDate] = useState(DEFAULT_START);
  const [endDate, setEndDate] = useState(DEFAULT_END);

  const [counts, setCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState("");

  const [perProjectRows, setPerProjectRows] = useState([]);
  const reqIdRef = useRef(0); 

  const [tableSort, setTableSort] = useState({ key: "name", dir: "asc" });

  useEffect(() => {
    if (tableSort.key === "name") {
      setTableSort({ key: "name", dir: sortAsc ? "asc" : "desc" });
    }
  }, [sortAsc]); 

  const sortedPerProjectRows = useMemo(() => {
    const rows = [...perProjectRows];
    const { key, dir } = tableSort;
    const mult = dir === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      if (key === "name") {
        const an = (a.name || "").toLowerCase();
        const bn = (b.name || "").toLowerCase();
        return mult * an.localeCompare(bn);
      }
      // numeric columns
      return mult * ((a[key] ?? 0) - (b[key] ?? 0));
    });
    return rows;
  }, [perProjectRows, tableSort]);

  // Load all projects once
  useEffect(() => {
    const go = async () => {
      try {
        setProjectsLoading(true);
        setProjectsError("");
        const resp = await getAllProjects(); // { error, message, data: [...] }
        const list = resp?.data ?? [];
        const mapped = list.map((p) => ({
          id: p.id ?? p.project_id ?? p._id,
          name: p.name ?? p.project_name ?? `Project ${p.id ?? ""}`,
          type: p.type ?? "",
          createdAt: p.createdAt,
          logo: p.logoUrl ?? p.logo ?? null,
        }));
        setProjects(mapped);
        if (mapped.length) setSelectedProjectId(mapped[0].id);
      } catch (e) {
        setProjectsError(e?.message || "Failed to load projects.");
      } finally {
        setProjectsLoading(false);
      }
    };
    go();
  }, []);

  const fetchSingleCounts = async (pid, s, e, currentReqId) => {
    try {
      setCountsLoading(true);
      setCountsError("");
      const resp = await getInteractionCounts(pid, s, e); 
      if (reqIdRef.current !== currentReqId) return; 
      setCounts(resp?.data ?? null);
      setPerProjectRows([]); 
    } catch (err) {
      if (reqIdRef.current !== currentReqId) return;
      setCounts(null);
      setPerProjectRows([]);
      setCountsError(err?.message || "Failed to load counts.");
    } finally {
      if (reqIdRef.current === currentReqId) setCountsLoading(false);
    }
  };

  const fetchAllCounts = async (s, e, currentReqId) => {
    try {
      setCountsLoading(true);
      setCountsError("");
      setPerProjectRows([]);

      const list = projects;
      if (!list.length) {
        setCounts(null);
        return;
      }

      const results = await Promise.allSettled(
        list.map(async (p) => {
          const resp = await getInteractionCounts(p.id, s, e);
          const data = resp?.data ?? null;
          return { project: p, data };
        })
      );

      if (reqIdRef.current !== currentReqId) return;

      let agg = {
        total_no_of_bot_messages: 0,
        total_no_of_human_messages: 0,
        total_interactions: 0,
        number_of_conversation: 0,
      };
      const rows = [];

      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value?.data) {
          const { project, data } = r.value;
          const row = {
            id: project.id,
            name: project.name,
            bot: data.total_no_of_bot_messages ?? 0,
            human: data.total_no_of_human_messages ?? 0,
            interactions: data.total_interactions ?? 0,
            conversations: data.number_of_conversation ?? 0,
          };
          rows.push(row);
          agg.total_no_of_bot_messages += row.bot;
          agg.total_no_of_human_messages += row.human;
          agg.total_interactions += row.interactions;
          agg.number_of_conversation += row.conversations;
        } else if (r.status === "fulfilled") {
          const { project } = r.value || {};
          if (project) {
            rows.push({
              id: project.id,
              name: project.name,
              bot: 0,
              human: 0,
              interactions: 0,
              conversations: 0,
            });
          }
        }
      });

      setCounts(agg);
      setPerProjectRows(rows);
    } catch (err) {
      if (reqIdRef.current !== currentReqId) return;
      setCounts(null);
      setPerProjectRows([]);
      setCountsError(err?.message || "Failed to load aggregated counts.");
    } finally {
      if (reqIdRef.current === currentReqId) setCountsLoading(false);
    }
  };

  const runFetch = (pid = selectedProjectId, s = startDate, e = endDate) => {
    const currentReqId = ++reqIdRef.current;
    if (!pid) return;
    if (pid === ALL_ID) return fetchAllCounts(s, e, currentReqId);
    return fetchSingleCounts(pid, s, e, currentReqId);
  };

  const handleExportCSV = () => {
    if (!isAll || !sortedPerProjectRows.length || !counts) return;
    const csv = rowsToCSV(sortedPerProjectRows, counts);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `insiderpers_chat_bot_counts_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleTableSort = (key) => {
    setTableSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }

      if (key === "name") return { key, dir: "asc" };
      return { key, dir: "desc" };
    });
  };

  const SortIndicator = ({ active, dir }) =>
    active ? (
      <span className="ml-1 text-[10px] align-middle">{dir === "asc" ? "▲" : "▼"}</span>
    ) : (
      <span className="ml-1 text-[10px] align-middle text-transparent">▲</span>
    );

  useEffect(() => {
    if (selectedProjectId) runFetch(selectedProjectId, startDate, endDate);
  }, [selectedProjectId]);

  return (
    <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 8px; }
        .custom-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 9999px; }
        .custom-scroll::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
        .dark .custom-scroll::-webkit-scrollbar-track { background: #1f2937; }
        .dark .custom-scroll::-webkit-scrollbar-thumb { background-color: #4b5563; }
        .dark .custom-scroll::-webkit-scrollbar-thumb:hover { background-color: #6b7280; }
      `}</style>

      <AdminHeader activePage="Chat Bot Summary" />

      <div className="flex">
        <aside className="hidden md:flex flex-col w-72 border-r dark:border-gray-800 bg-white dark:bg-gray-900 h-[calc(100vh-72px)] sticky top-[72px]">
          <div className="px-5 py-4 border-b dark:border-gray-800 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase text-gray-400 dark:text-gray-500">Projects</div>
              <button
                onClick={() => setSortAsc((v) => !v)}
                className="text-xs px-2 py-1 rounded border dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
                title={sortAsc ? "Sort Z–A" : "Sort A–Z"}
              >
                {sortAsc ? "A–Z" : "Z–A"}
              </button>
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects…"
              className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={() => setSelectedProjectId(ALL_ID)}
              className={`w-full text-left px-3 py-2 rounded-lg transition ${
                isAll
                  ? "bg-[#eef5fb] dark:bg-blue-900/30 text-[#32B1FF] font-medium"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              • All Projects
            </button>
          </div>

          <div className="flex-1 overflow-y-auto custom-scroll">
            {projectsLoading && (
              <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading projects…</div>
            )}
            {!projectsLoading && projectsError && (
              <div className="p-4 text-sm text-red-600 dark:text-red-400">{projectsError}</div>
            )}
            {!projectsLoading && !projectsError && displayProjects.length === 0 && (
              <div className="p-4 text-sm text-gray-500 dark:text-gray-400">No projects found.</div>
            )}

            {!projectsLoading &&
              !projectsError &&
              displayProjects.map((p) => {
                const active = String(p.id) === String(selectedProjectId);
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedProjectId(p.id)}
                    className={`w-full text-left px-4 py-3 border-b dark:border-gray-800 transition ${
                      active
                        ? "bg-[#eef5fb] dark:bg-blue-900/30 text-[#32B1FF] font-medium"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                    title={p.name}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-[#EAF6FF] dark:bg-blue-900/30 border border-[#C7E7FF] dark:border-blue-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {p.logo ? (
                          <img 
                            src={p.logo} 
                            alt={p.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <svg 
                          className="w-4 h-4" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#32B1FF" 
                          strokeWidth="2"
                          style={{ display: p.logo ? 'none' : 'block' }}
                        >
                          <rect x="3" y="4" width="18" height="16" rx="2"/>
                          <path d="M3 9h18M8 2v4M16 2v4"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{p.name}</div>
                        <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                          {p.type ? p.type.toUpperCase() : "—"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
          </div>
        </aside>

        <main className="flex-1">
          <div className="px-6 md:px-10 py-6 space-y-6">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100">Chat Bot Summary</h1>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedProject ? selectedProject.name : "Select a project"}
                </div>
              </div>

              <div className="flex items-center gap-2 bg-white dark:bg-gray-900 border dark:border-gray-800 rounded-xl p-2 shadow-sm">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400">Start</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border dark:border-gray-700 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400">End</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border dark:border-gray-700 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <button
                  onClick={() => runFetch()}
                  className="ml-2 px-3 py-1.5 rounded-lg bg-[#32B1FF] text-white text-sm font-semibold hover:bg-[#259ae6]"
                >
                  Apply
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow border dark:border-gray-800 p-5">
              {countsLoading && <div className="text-gray-500 dark:text-gray-400">Loading summary…</div>}
              {!countsLoading && countsError && (
                <div className="text-red-600 dark:text-red-400">{countsError}</div>
              )}

              {!countsLoading && !countsError && counts && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    <div className="relative overflow-hidden rounded-2xl p-5 shadow-sm border bg-gradient-to-br from-blue-500 to-sky-600 text-white">
                      <div className="absolute -top-6 -right-6 opacity-20">
                        <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                          <path d="M12 1v22M5 8v8M19 8v8"/>
                        </svg>
                      </div>
                      <div className="text-xs uppercase tracking-wide">Bot Messages</div>
                      <div className="mt-1 text-3xl font-extrabold drop-shadow-sm">
                        {fmt(counts.total_no_of_bot_messages)}
                      </div>
                      <div className="text-xs mt-1 text-white/80">Total across range</div>
                    </div>

                    <div className="relative overflow-hidden rounded-2xl p-5 shadow-sm border bg-gradient-to-br from-emerald-500 to-green-600 text-white">
                      <div className="absolute -top-6 -right-6 opacity-20">
                        <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                          <path d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5Z"/>
                          <path d="M19 22a7 7 0 0 0-14 0"/>
                        </svg>
                      </div>
                      <div className="text-xs uppercase tracking-wide">Human Messages</div>
                      <div className="mt-1 text-3xl font-extrabold drop-shadow-sm">
                        {fmt(counts.total_no_of_human_messages)}
                      </div>
                      <div className="text-xs mt-1 text-white/80">Total across range</div>
                    </div>

                    <div className="relative overflow-hidden rounded-2xl p-5 shadow-sm border bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                      <div className="absolute -top-6 -right-6 opacity-20">
                        <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                          <path d="M4 20V10M10 20V4M16 20v-6M22 20H2"/>
                        </svg>
                      </div>
                      <div className="text-xs uppercase tracking-wide">Total Interactions</div>
                      <div className="mt-1 text-3xl font-extrabold drop-shadow-sm">
                        {fmt(counts.total_interactions)}
                      </div>
                      <div className="text-xs mt-1 text-white/80">Bot + Human messages</div>
                    </div>

                    <div className="relative overflow-hidden rounded-2xl p-5 shadow-sm border bg-gradient-to-br from-rose-500 to-pink-600 text-white">
                      <div className="absolute -top-7 -right-8 opacity-20">
                        <svg width="110" height="110" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                          <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>
                        </svg>
                      </div>
                      <div className="text-xs uppercase tracking-wide"># of Conversations</div>
                      <div className="mt-1 text-3xl font-extrabold drop-shadow-sm">
                        {fmt(counts.number_of_conversation)}
                      </div>
                      <div className="text-xs mt-1 text-white/80">Unique conversations in range</div>
                    </div>
                  </div>

                  {isAll && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Per-Project Breakdown</div>
                        <button
                          onClick={handleExportCSV}
                          disabled={!sortedPerProjectRows.length}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold border dark:border-gray-700 transition ${
                            sortedPerProjectRows.length
                              ? "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                          }`}
                          title="Export as CSV"
                        >
                          Export CSV
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="min-w-full border dark:border-gray-800 rounded-lg overflow-hidden">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th
                                className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-2 border-b dark:border-gray-700 cursor-pointer select-none"
                                onClick={() => toggleTableSort("name")}
                              >
                                Project
                                <SortIndicator
                                  active={tableSort.key === "name"}
                                  dir={tableSort.dir}
                                />
                              </th>
                              <th
                                className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-2 border-b dark:border-gray-700 cursor-pointer select-none"
                                onClick={() => toggleTableSort("bot")}
                              >
                                Bot
                                <SortIndicator
                                  active={tableSort.key === "bot"}
                                  dir={tableSort.dir}
                                />
                              </th>
                              <th
                                className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-2 border-b dark:border-gray-700 cursor-pointer select-none"
                                onClick={() => toggleTableSort("human")}
                              >
                                Human
                                <SortIndicator
                                  active={tableSort.key === "human"}
                                  dir={tableSort.dir}
                                />
                              </th>
                              <th
                                className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-2 border-b dark:border-gray-700 cursor-pointer select-none"
                                onClick={() => toggleTableSort("interactions")}
                              >
                                Interactions
                                <SortIndicator
                                  active={tableSort.key === "interactions"}
                                  dir={tableSort.dir}
                                />
                              </th>
                              <th
                                className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-2 border-b dark:border-gray-700 cursor-pointer select-none"
                                onClick={() => toggleTableSort("conversations")}
                              >
                                Conversations
                                <SortIndicator
                                  active={tableSort.key === "conversations"}
                                  dir={tableSort.dir}
                                />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedPerProjectRows.map((row) => (
                              <tr key={row.id} className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800">
                                <td className="px-4 py-2 border-b dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">{row.name}</td>
                                <td className="px-4 py-2 border-b dark:border-gray-700 text-right text-sm text-gray-900 dark:text-gray-100">{fmt(row.bot)}</td>
                                <td className="px-4 py-2 border-b dark:border-gray-700 text-right text-sm text-gray-900 dark:text-gray-100">{fmt(row.human)}</td>
                                <td className="px-4 py-2 border-b dark:border-gray-700 text-right text-sm text-gray-900 dark:text-gray-100">{fmt(row.interactions)}</td>
                                <td className="px-4 py-2 border-b dark:border-gray-700 text-right text-sm text-gray-900 dark:text-gray-100">{fmt(row.conversations)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-gray-100 dark:bg-gray-800">
                              <td className="px-4 py-2 border-t dark:border-gray-700 font-semibold text-sm text-gray-700 dark:text-gray-300">Total</td>
                              <td className="px-4 py-2 border-t dark:border-gray-700 text-right font-semibold text-sm text-gray-900 dark:text-gray-100">{fmt(counts.total_no_of_bot_messages)}</td>
                              <td className="px-4 py-2 border-t dark:border-gray-700 text-right font-semibold text-sm text-gray-900 dark:text-gray-100">{fmt(counts.total_no_of_human_messages)}</td>
                              <td className="px-4 py-2 border-t dark:border-gray-700 text-right font-semibold text-sm text-gray-900 dark:text-gray-100">{fmt(counts.total_interactions)}</td>
                              <td className="px-4 py-2 border-t dark:border-gray-700 text-right font-semibold text-sm text-gray-900 dark:text-gray-100">{fmt(counts.number_of_conversation)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {!countsLoading && !countsError && !counts && (
                <div className="text-gray-500 dark:text-gray-400">Select a project to view summary.</div>
              )}
            </div>

            <div className="text-xs text-gray-400 dark:text-gray-500">
              Default range is <span className="font-semibold">July 1–31, 2025</span>. Adjust dates to re-query.
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CountSummaryPage;
